import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isHotkey } from 'is-hotkey'
import {
  Descendant,
  Editor,
  Element as SlateElement,
  Node,
  Path,
  Range,
  Text,
  Transforms,
} from 'slate'
import {
  Editable,
  ReactEditor,
  RenderElementProps,
  RenderLeafProps,
  Slate,
} from 'slate-react'

import { MentionItem } from '@/components/MarkdownDisplay'
import type { User } from '@/schema'
import { cn, formatFullName } from '@/utils'
import { DEFAULT_MAX_CHARACTERS } from '@/utils/constants'

import {
  DEFAULT_EDITOR_ID,
  DEFAULT_MODE,
  EditorMode,
  MENTION_PATTERN_END_REGEX,
  REPLY_EDITOR_ID,
} from '../../constants'
import type { EditorModeType } from '../../types'
import type { MentionElement } from '../../types/slate'
import { processPaste } from '../../utils/pasteHandler'
import {
  countCharacters,
  createEditor,
  initialEditorValue,
  insertMarkdownSyntax,
  parseMarkdownToSlate,
  serializeToMarkdown,
  toggleMark,
} from '../../utils/slateUtils'
import { type CommentTemplate, CommentTemplateDialog } from '../CommentTemplate'

import { DEFAULT_TOOLBAR_ICON_SIZE, EditorToolbar } from './EditorToolbar'
import { MentionPopover } from './MentionPopover'

const HOTKEYS: Record<string, string> = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+`': 'code',
}

const EDITOR_BASE_CLASSES =
  'w-full min-h-[80px] overflow-y-auto px-4 pt-2 focus:border-none focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-60'

const HEADING_BASE_CLASSES = 'font-bold mb-2'
const HEADING_LEVEL_CLASSES = {
  1: 'text-3xl',
  2: 'text-2xl',
}

const ELEMENT_TYPE_HEADING = 'heading'
const ELEMENT_TYPE_BLOCK_QUOTE = 'block-quote'
const ELEMENT_TYPE_BULLETED_LIST = 'bulleted-list'
const ELEMENT_TYPE_NUMBERED_LIST = 'numbered-list'
const ELEMENT_TYPE_LIST_ITEM = 'list-item'
const ELEMENT_TYPE_MENTION = 'mention'

const LIST_BASE_CLASSES = 'mb-2 ml-6'
const PARAGRAPH_CLASSES = 'my-1'
const LIST_ITEM_CLASSES = 'mb-1'
const BLOCK_QUOTE_CLASSES =
  'border border-l-2 border-solid border-white border-l-gray-light-200   ml-0 mr-0 pl-2 text-gray-light-400 italic'

/** True when value has exactly one block with exactly one child that is a mention (no text node). */
function isValueMentionOnly(value: Descendant[]): boolean {
  if (value.length !== 1) return false
  const block = value[0]
  if (!SlateElement.isElement(block) || !block.children?.length) return false
  if (block.children.length !== 1) return false
  const child = block.children[0]
  return SlateElement.isElement(child) && child.type === 'mention'
}

export interface RichTextEditorProps {
  value: Descendant[]
  onChange: (value: Descendant[]) => void
  maxCharacters?: number
  disabled?: boolean
  autoFocus?: boolean
  id?: string
  'aria-describedby'?: string
  defaultMode?: EditorModeType
  mode?: EditorModeType
  onCompositionStart?: () => void
  onCompositionEnd?: () => void
  placeholder?: string
  showSendButton?: boolean
  onSend?: () => void
  isSending?: boolean
  toolbarIconSize?: number
  className?: string
  showHorizontalScroll?: boolean
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = initialEditorValue,
  onChange,
  maxCharacters = DEFAULT_MAX_CHARACTERS,
  disabled = false,
  autoFocus = false,
  id = DEFAULT_EDITOR_ID,
  'aria-describedby': ariaDescribedBy,
  defaultMode = DEFAULT_MODE,
  mode: controlledMode,
  onCompositionStart,
  onCompositionEnd,
  placeholder,
  showSendButton = false,
  onSend,
  isSending = false,
  toolbarIconSize = DEFAULT_TOOLBAR_ICON_SIZE,
  className,
  showHorizontalScroll = false,
}) => {
  const { t } = useTranslation()

  // Force plain mode for thread reply editor
  // Use controlled mode if provided, otherwise use internal state
  const effectiveMode = controlledMode ?? defaultMode
  const mode = id === REPLY_EDITOR_ID ? EditorMode.PLAIN : effectiveMode

  const editor = useMemo(() => {
    const e = createEditor()
    // Configure mentions as inline void elements
    const { isInline, isVoid } = e
    e.isInline = (element) => {
      return element.type === 'mention' ? true : isInline(element)
    }
    e.isVoid = (element) => {
      return element.type === 'mention' ? true : isVoid(element)
    }
    return e
  }, [])

  // Track focus state for placeholder visibility
  const [isFocused, setIsFocused] = useState(false)

  // Track raw markdown text for markdown mode
  // Initialize with markdown if starting in markdown mode
  // Skip for thread reply editor (only plain mode allowed)
  const [rawMarkdown, setRawMarkdown] = useState(() =>
    mode === EditorMode.MARKDOWN && id !== REPLY_EDITOR_ID
      ? serializeToMarkdown(value)
      : ''
  )
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const previousMode = useRef<EditorModeType>(mode)
  const isMaximumCharacter = countCharacters(value) >= maxCharacters

  // Mention state
  const [mentionState, setMentionState] = useState<{
    isOpen: boolean
    searchQuery: string
    position: { top: number; left: number } | null
    triggerPosition: number | null
  }>({
    isOpen: false,
    searchQuery: '',
    position: null,
    triggerPosition: null,
  })
  const mentionTriggerRef = useRef<number | null>(null)

  // Comment template dialog state
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false)

  // Get cursor position for mention popover
  const getCursorPosition = useCallback(() => {
    if (mode === EditorMode.MARKDOWN && textareaRef.current) {
      const textarea = textareaRef.current
      const selectionStart = textarea.selectionStart
      // Use textarea.value instead of rawMarkdown to ensure we have the latest text
      // rawMarkdown state might not be updated yet when this is called
      const textBeforeCursor = textarea.value.substring(0, selectionStart)

      // Get textarea styles
      const computedStyle = globalThis.getComputedStyle(textarea)
      const textareaRect = textarea.getBoundingClientRect()

      // Create a mirror div that matches textarea styling exactly
      const mirror = document.createElement('div')
      mirror.style.position = 'absolute'
      mirror.style.visibility = 'hidden'
      mirror.style.whiteSpace = 'pre-wrap'
      mirror.style.overflowWrap = 'break-word'
      mirror.style.width = `${textarea.clientWidth}px`
      mirror.style.font = computedStyle.font
      mirror.style.fontSize = computedStyle.fontSize
      mirror.style.fontFamily = computedStyle.fontFamily
      mirror.style.fontWeight = computedStyle.fontWeight
      mirror.style.lineHeight = computedStyle.lineHeight
      mirror.style.padding = computedStyle.padding
      mirror.style.border = computedStyle.border
      mirror.style.boxSizing = computedStyle.boxSizing
      mirror.style.letterSpacing = computedStyle.letterSpacing
      mirror.style.textTransform = computedStyle.textTransform

      // Set text content up to cursor (use non-breaking space if empty to ensure measurement)
      mirror.textContent = textBeforeCursor || '\u00A0'

      // Add to DOM temporarily to measure
      document.body.appendChild(mirror)

      try {
        // Get the position of the last character (cursor position)
        const range = document.createRange()
        const textNode = mirror.firstChild

        if (textNode?.nodeType === 3) {
          const textLength = textNode.textContent?.length ?? 0
          const cursorPos = Math.max(0, Math.min(textLength, textLength))
          range.setStart(textNode, cursorPos)
          range.setEnd(textNode, cursorPos)
        } else {
          // Fallback: measure from the end of the mirror div
          range.selectNodeContents(mirror)
          range.collapse(false) // Collapse to end
        }

        const rangeRect = range.getBoundingClientRect()
        const mirrorRect = mirror.getBoundingClientRect()

        // Calculate position relative to textarea
        // Account for padding and border
        const paddingTop = Number.parseFloat(computedStyle.paddingTop) || 0
        const paddingLeft = Number.parseFloat(computedStyle.paddingLeft) || 0
        const borderTop = Number.parseFloat(computedStyle.borderTopWidth) || 0
        const borderLeft = Number.parseFloat(computedStyle.borderLeftWidth) || 0
        const lineHeight = Number.parseFloat(computedStyle.lineHeight) || 20

        // Position at the bottom of the cursor line (below the text)
        const top =
          textareaRect.top +
          paddingTop +
          borderTop +
          (rangeRect.top - mirrorRect.top) +
          lineHeight

        const left =
          textareaRect.left +
          paddingLeft +
          borderLeft +
          (rangeRect.left - mirrorRect.left)

        // Clean up
        mirror.remove()

        const position = {
          top: top + window.scrollY,
          left: left + window.scrollX,
        }
        return position
      } catch {
        // Clean up on error
        if (mirror.parentNode) {
          mirror.remove()
        }
        // Fallback: position at textarea top-right
        const fallbackPosition = {
          top: textareaRect.top + window.scrollY + 20,
          left: textareaRect.left + window.scrollX,
        }
        return fallbackPosition
      }
    }

    // For Slate editor
    try {
      const { selection } = editor
      if (!selection) return null

      const domRange = ReactEditor.toDOMRange(editor, selection)
      const rect = domRange.getBoundingClientRect()
      const position = {
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
      }
      return position
    } catch {
      return null
    }
  }, [mode, editor])

  // Helper to reset selection to start of document
  const resetSelectionToStart = useCallback(() => {
    try {
      Transforms.select(editor, Editor.start(editor, [0, 0]))
      return editor.selection
    } catch {
      return null
    }
  }, [editor])

  // Helper to handle mention node selection - finds or inserts text node after mention
  const handleMentionNodeSelection = useCallback(
    (point: { path: number[]; offset: number }) => {
      const blockPath = point.path.slice(0, -1)
      const mentionIndex = point.path[point.path.length - 1]

      try {
        const blockNode = Node.get(editor, blockPath)
        if (!SlateElement.isElement(blockNode)) {
          return resetSelectionToStart()
        }

        // Try to find a text node after the mention
        if (mentionIndex + 1 < blockNode.children.length) {
          const nextNode = blockNode.children[mentionIndex + 1]
          if (Text.isText(nextNode)) {
            const newPath = [...blockPath, mentionIndex + 1]
            Transforms.select(editor, {
              anchor: { path: newPath, offset: 0 },
              focus: { path: newPath, offset: 0 },
            })
            return editor.selection
          }
        }

        // If no text node found, insert one
        const insertPath = [...blockPath, mentionIndex + 1]
        try {
          Transforms.insertNodes(editor, { text: '' }, { at: insertPath })
          Transforms.select(editor, {
            anchor: { path: insertPath, offset: 0 },
            focus: { path: insertPath, offset: 0 },
          })
          return editor.selection
        } catch {
          // Fallback: try to select at the end of the block
          try {
            const lastChildIndex = blockNode.children.length - 1
            const lastChild = blockNode.children[lastChildIndex]
            if (Text.isText(lastChild)) {
              const lastPath = [...blockPath, lastChildIndex]
              Transforms.select(editor, {
                anchor: { path: lastPath, offset: lastChild.text.length },
                focus: { path: lastPath, offset: lastChild.text.length },
              })
              return editor.selection
            }
          } catch {
            // Final fallback: reset to start
            return resetSelectionToStart()
          }
          return resetSelectionToStart()
        }
      } catch {
        return resetSelectionToStart()
      }
    },
    [editor, resetSelectionToStart]
  )

  // Helper to validate and normalize a single point (start or end)
  const normalizePoint = useCallback(
    (point: { path: number[]; offset: number }) => {
      try {
        const node = Node.get(editor, point.path)
        if (!node) {
          return resetSelectionToStart()
        }

        // If it's a mention element, handle it specially (cursor on the mention node)
        if (SlateElement.isElement(node) && node.type === 'mention') {
          return handleMentionNodeSelection(point)
        }

        // If it's a text node inside a mention (void element), move cursor after the mention
        // Path [0, 0, 0] = text inside mention at [0, 0]; Slate doesn't allow typing there
        if (Text.isText(node) && point.path.length > 1) {
          const parentPath = Path.parent(point.path)
          try {
            const parentNode = Node.get(editor, parentPath)
            if (
              SlateElement.isElement(parentNode) &&
              parentNode.type === 'mention'
            ) {
              return handleMentionNodeSelection({
                path: parentPath,
                offset: 0,
              })
            }
          } catch {
            // parent path invalid
          }
        }

        // If it's a text node with invalid offset, adjust it
        if (Text.isText(node) && point.offset > node.text.length) {
          Transforms.select(editor, {
            anchor: { path: point.path, offset: node.text.length },
            focus: { path: point.path, offset: node.text.length },
          })
          return editor.selection
        }

        return null // Point is valid
      } catch {
        return resetSelectionToStart()
      }
    },
    [editor, handleMentionNodeSelection, resetSelectionToStart]
  )

  // Helper to validate and normalize selection
  const normalizeSelection = useCallback(() => {
    try {
      const { selection } = editor
      if (!selection) {
        return resetSelectionToStart()
      }

      const [start, end] = Range.edges(selection)

      // Validate start point
      const startResult = normalizePoint(start)
      if (startResult) {
        return startResult
      }

      // Validate end point
      const endResult = normalizePoint(end)
      if (endResult) {
        return endResult
      }

      return selection
    } catch {
      return resetSelectionToStart()
    }
  }, [editor, normalizePoint, resetSelectionToStart])

  // Reset rawMarkdown when value is reset to initial (e.g., when dialog closes)
  // Skip for thread reply editor (only plain mode allowed)
  useEffect(() => {
    if (id === REPLY_EDITOR_ID) return

    // Check if value is effectively empty (initial state)
    // Serialize to markdown and check if it's empty
    const markdownFromValue = serializeToMarkdown(value)
    const isValueEmpty = markdownFromValue.trim() === ''

    if (
      isValueEmpty &&
      mode === EditorMode.MARKDOWN &&
      rawMarkdown.trim() !== ''
    ) {
      setRawMarkdown('')
    }
  }, [value, mode, rawMarkdown, id])

  // Sync raw markdown with Slate value when switching modes
  // Skip for thread reply editor (only plain mode allowed)
  useEffect(() => {
    if (id === REPLY_EDITOR_ID) {
      previousMode.current = mode
      return
    }

    // When switching TO markdown mode, sync rawMarkdown from Slate value
    if (
      mode === EditorMode.MARKDOWN &&
      previousMode.current !== EditorMode.MARKDOWN
    ) {
      const markdown = serializeToMarkdown(value)
      setRawMarkdown(markdown)
    }
    // When switching FROM markdown mode to plain mode, parse rawMarkdown and update value
    else if (
      mode === EditorMode.PLAIN &&
      previousMode.current === EditorMode.MARKDOWN &&
      rawMarkdown
    ) {
      try {
        const parsedNodes = parseMarkdownToSlate(rawMarkdown)
        // Use setTimeout to ensure this happens after the mode switch is complete
        // and to avoid conflicts with other state updates
        setTimeout(() => {
          onChange(parsedNodes)
        }, 0)
      } catch {
        // On error, fallback to initial value
        setTimeout(() => {
          onChange(initialEditorValue)
        }, 0)
      }
    }
    previousMode.current = mode
  }, [mode, value, rawMarkdown, onChange, id])

  // Normalize selection when switching modes to prevent invalid path errors
  useEffect(() => {
    if (mode === EditorMode.PLAIN) {
      // When switching to plain mode, normalize the selection
      // This prevents errors when the selection path is invalid after parsing markdown
      // Use setTimeout to ensure value has been updated first
      setTimeout(() => {
        try {
          normalizeSelection()
        } catch {
          // Silently handle errors - selection will be reset on next interaction
        }
      }, 0)
    }
  }, [mode, value, normalizeSelection])

  // Detect @ in plain mode after it's inserted
  useEffect(() => {
    if (mode !== EditorMode.PLAIN || mentionState.isOpen) return

    // Normalize selection to ensure it's valid
    const validSelection = normalizeSelection()
    if (!validSelection) return

    try {
      const [start] = Range.edges(validSelection)
      const beforeText = Editor.string(editor, {
        anchor: Editor.start(editor, start.path),
        focus: start,
      })

      // Check if text ends with @ and it's not already a mention pattern
      if (
        beforeText.endsWith('@') &&
        !MENTION_PATTERN_END_REGEX.exec(beforeText)
      ) {
        const position = getCursorPosition()
        if (position) {
          setMentionState({
            isOpen: true,
            searchQuery: '',
            position,
            triggerPosition: start.offset,
          })
          mentionTriggerRef.current = start.offset
        }
      }
    } catch {
      // Silently handle errors - selection might be invalid after mode switch
    }
  }, [
    value,
    mode,
    editor,
    getCursorPosition,
    mentionState.isOpen,
    normalizeSelection,
  ])

  // Update searchQuery when value changes in plain mode with mention context open
  useEffect(() => {
    if (mode !== EditorMode.PLAIN || !mentionState.isOpen) return

    // Use setTimeout to ensure Slate has finished processing the change
    // The value prop is already updated, we just need to wait for Slate's internal state to sync
    const timeoutId = setTimeout(() => {
      try {
        const { selection } = editor
        if (!selection) {
          return
        }

        const [start] = Range.edges(selection)
        // Read text from the updated value instead of editor to get the latest text
        // Create a temporary editor with the updated value to read the text
        const tempEditor = createEditor()
        tempEditor.children = value
        const beforeText = Editor.string(tempEditor, {
          anchor: Editor.start(tempEditor, start.path),
          focus: start,
        })

        const atIndex = beforeText.lastIndexOf('@')
        if (atIndex === -1) {
          // No @ found, close mention
          setMentionState({
            isOpen: false,
            searchQuery: '',
            position: null,
            triggerPosition: null,
          })
          mentionTriggerRef.current = null
          return
        }

        // Check if it's already a mention pattern
        if (MENTION_PATTERN_END_REGEX.exec(beforeText)) {
          // It's a mention pattern, close the popover
          setMentionState({
            isOpen: false,
            searchQuery: '',
            position: null,
            triggerPosition: null,
          })
          mentionTriggerRef.current = null
          return
        }

        const query = beforeText.substring(atIndex + 1).trim()
        const position = getCursorPosition()
        if (position) {
          setMentionState((prev) => ({
            ...prev,
            searchQuery: query,
            position,
          }))
          // Preserve focus and selection after updating state
          requestAnimationFrame(() => {
            ReactEditor.focus(editor)
            const currentSelection = editor.selection
            if (currentSelection) {
              Transforms.select(editor, currentSelection)
            }
          })
        }
      } catch {
        // Silently handle errors
      }
    }, 10) // Small delay to ensure Slate has processed the change

    return () => clearTimeout(timeoutId)
  }, [
    value,
    mode,
    editor,
    mentionState.isOpen,
    mentionState.searchQuery,
    getCursorPosition,
    id,
  ])

  // Handle value changes with character limit enforcement
  const handleValueChange = useCallback(
    (newValue: Descendant[]) => {
      const characterCount = countCharacters(newValue)
      if (characterCount > maxCharacters) {
        return
      }
      onChange(newValue)
    },
    [onChange, maxCharacters]
  )

  // Handle raw markdown text changes
  const handleRawMarkdownChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newText = event.target.value

      // Check character limit
      if (newText.length > maxCharacters) {
        return // Don't allow input beyond limit
      }

      setRawMarkdown(newText)

      // Check for mention trigger after text is updated
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        const textarea = event.target
        if (!textarea) return

        const start = textarea.selectionStart
        const beforeText = newText.substring(0, start)
        const atIndex = beforeText.lastIndexOf('@')

        if (atIndex === -1) {
          // No @ found, close mention
          setMentionState({
            isOpen: false,
            searchQuery: '',
            position: null,
            triggerPosition: null,
          })
          mentionTriggerRef.current = null
          return
        }

        // Check if it's a mention pattern (already inserted)
        const mentionPattern = /@\[[^\]]+\]\(user:[^)]+\)/
        const textAfterAt = beforeText.substring(atIndex)
        const isMentionPattern = mentionPattern.test(textAfterAt)

        if (isMentionPattern) {
          // It's a mention pattern, close the popover
          setMentionState({
            isOpen: false,
            searchQuery: '',
            position: null,
            triggerPosition: null,
          })
          mentionTriggerRef.current = null
          return
        }

        // Extract query from @ to cursor
        const query = beforeText.substring(atIndex + 1, start)

        // Check if query is valid (not containing spaces or newlines)
        if (
          query.includes(' ') ||
          query.includes('\n') ||
          query.includes('\t')
        ) {
          // Close mention if space or newline found
          setMentionState({
            isOpen: false,
            searchQuery: '',
            position: null,
            triggerPosition: null,
          })
          mentionTriggerRef.current = null
          return
        }

        // Update mention state with query
        const position = getCursorPosition()
        if (position) {
          setMentionState({
            isOpen: true,
            searchQuery: query.trim(),
            position,
            triggerPosition: atIndex,
          })
          mentionTriggerRef.current = atIndex
          // Keep focus on textarea
          setTimeout(() => {
            if (textarea) {
              textarea.focus()
              textarea.setSelectionRange(start, start)
            }
          }, 0)
        }
      })

      // Parse markdown and update Slate value
      try {
        const parsedNodes = parseMarkdownToSlate(newText)
        onChange(parsedNodes)
      } catch {
        // Still update the raw text even if parsing fails
      }
    },
    [onChange, maxCharacters, getCursorPosition]
  )

  // Handle inserting markdown syntax at cursor position
  const handleInsertMarkdown = useCallback(
    (syntax: string, wrapText: boolean = false) => {
      const textarea = textareaRef.current
      if (!textarea) return

      const start = textarea.selectionStart
      const end = textarea.selectionEnd

      const result = insertMarkdownSyntax(
        rawMarkdown,
        syntax,
        wrapText,
        start,
        end,
        maxCharacters
      )

      if (!result) {
        // Character limit exceeded
        return
      }

      const { newText, newCursorPosition } = result

      setRawMarkdown(newText)

      // Parse and update
      try {
        const parsedNodes = parseMarkdownToSlate(newText)
        onChange(parsedNodes)
      } catch {
        // Failed to parse markdown - silently fail
      }

      // Restore cursor position after React updates
      setTimeout(() => {
        if (textarea) {
          textarea.focus()
          textarea.setSelectionRange(newCursorPosition, newCursorPosition)
        }
      }, 0)
    },
    [rawMarkdown, onChange, maxCharacters]
  )

  const insertMarkdownTemplate = useCallback(
    (template: CommentTemplate) => {
      // For markdown mode, append to raw markdown
      const newText = rawMarkdown
        ? rawMarkdown + ' ' + template.content
        : template.content

      if (newText.length > maxCharacters) {
        return // Don't insert if it would exceed limit
      }

      setRawMarkdown(newText)
      try {
        const parsedNodes = parseMarkdownToSlate(newText)
        onChange(parsedNodes)
      } catch {
        // Failed to parse markdown
      }
    },
    [rawMarkdown, maxCharacters, onChange]
  )

  const insertPlainTemplate = useCallback(
    (template: CommentTemplate) => {
      // For plain mode, insert at cursor position (append, don't replace)
      try {
        // Normalize newlines to avoid creating multiple paragraphs
        const normalizedContent = template.content.replace(/\n\n+/g, '\n') // NOSONAR
        const templateNodes = parseMarkdownToSlate(normalizedContent)

        // Move cursor to end if no selection
        if (!editor.selection) {
          Transforms.select(editor, Editor.end(editor, []))
        }

        // Insert at current cursor position
        // If the template is a single paragraph, insert its children to avoid creating a new block
        if (
          templateNodes.length === 1 &&
          'type' in templateNodes[0] &&
          templateNodes[0].type === 'paragraph'
        ) {
          Transforms.insertNodes(editor, templateNodes[0].children)
        } else {
          Transforms.insertNodes(editor, templateNodes)
        }

        // Explicitly call onChange to sync parent state
        onChange(editor.children)
      } catch {
        // Failed to parse template
      }
    },
    [editor, onChange]
  )

  // Handle template selection - insert template content into editor
  const handleTemplateSelect = useCallback(
    (template: CommentTemplate) => {
      if (mode === EditorMode.MARKDOWN) {
        insertMarkdownTemplate(template)
      } else {
        insertPlainTemplate(template)
      }
      setIsTemplateDialogOpen(false)
    },
    [mode, insertMarkdownTemplate, insertPlainTemplate]
  )

  // Insert mention in plain mode (Slate)
  const insertMention = useCallback((editor: Editor, user: User) => {
    if (!user.id) return

    const fullName = formatFullName(user.first_name, user.last_name)
    const mention: MentionElement = {
      type: 'mention',
      userId: user.id,
      userName: fullName,
      userEmail: user.email || '',
      children: [{ text: '' }],
    }

    // Find the @ trigger and replace it with the mention
    const { selection } = editor
    if (!selection) return

    const [start] = Range.edges(selection)
    const beforeText = Editor.string(editor, {
      anchor: Editor.start(editor, start.path),
      focus: start,
    })

    // Find the last @ before cursor
    const atIndex = beforeText.lastIndexOf('@')
    if (atIndex === -1) return

    // Calculate the range to replace
    const path = start.path
    const offset = start.offset
    const replaceStart = offset - (beforeText.length - atIndex)

    // Delete text from @ to cursor
    Transforms.delete(editor, {
      at: {
        path,
        offset: replaceStart,
      },
      distance: offset - replaceStart,
    })

    // Insert mention
    Transforms.insertNodes(editor, mention, {
      at: { path, offset: replaceStart },
    })

    // Move cursor after mention
    Transforms.move(editor, { distance: 1, unit: 'offset' })
  }, [])

  // Insert mention in markdown mode
  const insertMentionMarkdown = useCallback(
    (user: User) => {
      if (!user.id) return

      const textarea = textareaRef.current
      if (!textarea) return

      const start = textarea.selectionStart
      const beforeText = rawMarkdown.substring(0, start)
      const atIndex = beforeText.lastIndexOf('@')

      if (atIndex === -1) return

      const fullName = formatFullName(user.first_name, user.last_name)
      // Use markdown format: @[username](user:userId)
      const mentionText = `@[${fullName}](user:${user.id})`

      // Replace @query with @[username](user:userId)
      const newText =
        rawMarkdown.substring(0, atIndex) +
        mentionText +
        rawMarkdown.substring(start)

      // Check character limit
      if (newText.length > maxCharacters) {
        return
      }

      setRawMarkdown(newText)

      // Parse and update
      try {
        const parsedNodes = parseMarkdownToSlate(newText)
        onChange(parsedNodes)
      } catch {
        // Failed to parse markdown - silently fail
      }

      // Set cursor position after mention
      const newCursorPosition = atIndex + mentionText.length
      setTimeout(() => {
        if (textarea) {
          textarea.focus()
          textarea.setSelectionRange(newCursorPosition, newCursorPosition)
        }
      }, 0)
    },
    [rawMarkdown, onChange, maxCharacters]
  )

  // Handle mention selection
  const handleMentionSelect = useCallback(
    (user: User) => {
      if (mode === EditorMode.MARKDOWN) {
        insertMentionMarkdown(user)
      } else {
        insertMention(editor, user)
      }
      setMentionState({
        isOpen: false,
        searchQuery: '',
        position: null,
        triggerPosition: null,
      })
      mentionTriggerRef.current = null
    },
    [mode, editor, insertMention, insertMentionMarkdown]
  )

  // Helper: true when @ was just typed (for opening mention popover in plain mode)
  const isAtKeyJustTyped = (
    key: string,
    keyLength: number,
    beforeText: string
  ): boolean => key === '@' || (keyLength === 1 && beforeText.endsWith('@'))

  // Detect mention trigger in plain mode
  const detectMentionPlain = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const { selection } = editor
      if (!selection) return false

      const [start] = Range.edges(selection)
      let beforeText = ''
      try {
        beforeText = Editor.string(editor, {
          anchor: Editor.start(editor, start.path),
          focus: start,
        })
      } catch {
        return false
      }

      const inMentionContext =
        mentionState.isOpen || mentionTriggerRef.current !== null

      // Check if we're in a mention context FIRST (typing after @)
      // This must come before checking if @ was just typed to avoid false positives
      if (inMentionContext) {
        const atIndex = beforeText.lastIndexOf('@')

        if (atIndex === -1) {
          // No @ found, close mention
          setMentionState({
            isOpen: false,
            searchQuery: '',
            position: null,
            triggerPosition: null,
          })
          mentionTriggerRef.current = null
        } else {
          // Close mention if space, newline, or special character
          if (shouldCloseMention(event.key)) {
            setMentionState({
              isOpen: false,
              searchQuery: '',
              position: null,
              triggerPosition: null,
            })
            mentionTriggerRef.current = null
            return false
          }

          // For Backspace/Delete, read the actual text after deletion
          // This prevents keyDown from overwriting with stale query
          if (event.key === 'Backspace' || event.key === 'Delete') {
            // Read the actual text after deletion is processed
            // Use setTimeout to wait for onChange to update the value prop
            setTimeout(() => {
              const { selection } = editor
              if (!selection) {
                return
              }

              const [start] = Range.edges(selection)
              // Read from value prop to get the latest text after deletion
              const tempEditor = createEditor()
              tempEditor.children = value
              const afterText = Editor.string(tempEditor, {
                anchor: Editor.start(tempEditor, start.path),
                focus: start,
              })

              const atIndex = afterText.lastIndexOf('@')
              if (atIndex === -1) {
                // No @ found, close mention
                setMentionState({
                  isOpen: false,
                  searchQuery: '',
                  position: null,
                  triggerPosition: null,
                })
                mentionTriggerRef.current = null
                return
              }

              const query = afterText.substring(atIndex + 1).trim()
              const position = getCursorPosition()
              if (position) {
                setMentionState((prev) => ({
                  ...prev,
                  searchQuery: query,
                  position,
                }))
              }
              // Keep focus on editor
              ReactEditor.focus(editor)
              const currentSelection = editor.selection
              if (currentSelection) {
                Transforms.select(editor, currentSelection)
              }
            }, 10) // Small delay to ensure value prop is updated by onChange
            // Return true to indicate we handled it and popover should stay open
            return true
          }

          // For regular characters, let Slate insert them and let the useEffect handle query update
          // Just ensure focus is maintained - don't try to read text here as it's not updated yet
          requestAnimationFrame(() => {
            ReactEditor.focus(editor)
            const currentSelection = editor.selection
            if (currentSelection) {
              Transforms.select(editor, currentSelection)
            }
          })
          // Return true to indicate we're in mention context
          // The onChange useEffect will update the searchQuery after Slate processes the character
          return true
        }
      }

      // Check if @ was just typed (only if NOT already in mention context)
      if (isAtKeyJustTyped(event.key, event.key.length, beforeText)) {
        const position = getCursorPosition()
        if (position) {
          // Preserve selection before opening popover
          const selectionToPreserve = { ...selection }

          setMentionState({
            isOpen: true,
            searchQuery: '',
            position,
            triggerPosition: start.offset,
          })
          mentionTriggerRef.current = start.offset

          // Keep focus on editor immediately and preserve selection
          // Use multiple requestAnimationFrame calls to ensure it happens after state update
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              ReactEditor.focus(editor)
              // Restore the preserved selection
              try {
                Transforms.select(editor, selectionToPreserve)
              } catch {
                // If selection is invalid, try to restore to the start position
                try {
                  Transforms.select(editor, {
                    anchor: start,
                    focus: start,
                  })
                } catch {
                  // If that fails, just focus the editor
                  ReactEditor.focus(editor)
                }
              }
            })
          })
          // Don't return true here - let the @ character be inserted
          return false
        }
      }

      return false
    },
    [editor, mentionState.isOpen, getCursorPosition, value]
  )

  // Detect mention trigger in markdown mode
  // Helper: Check if a character is valid for mention search
  const isValidSearchCharacter = (
    key: string,
    ctrlKey: boolean,
    metaKey: boolean
  ): boolean => {
    if (key.length !== 1 || ctrlKey || metaKey) {
      return false
    }
    return key !== ' ' && key !== '\n' && key !== '\t' && key !== 'Enter'
  }

  // Helper: Check if mention should be closed based on key
  const shouldCloseMention = (key: string): boolean => {
    return (
      key === ' ' ||
      key === 'Enter' ||
      key === 'Escape' ||
      key === '\n' ||
      key === '\t'
    )
  }

  // Helper: Close mention popover
  const closeMentionPopover = useCallback(() => {
    setMentionState({
      isOpen: false,
      searchQuery: '',
      position: null,
      triggerPosition: null,
    })
    mentionTriggerRef.current = null
  }, [])

  // Helper: Handle @ key in markdown mode
  const handleAtKeyInMarkdown = useCallback(
    (textarea: HTMLTextAreaElement, start: number) => {
      const position = getCursorPosition()
      if (position) {
        setMentionState({
          isOpen: true,
          searchQuery: '',
          position,
          triggerPosition: start,
        })
        mentionTriggerRef.current = start
        setTimeout(() => {
          if (textarea) {
            textarea.focus()
            textarea.setSelectionRange(start, start)
          }
        }, 0)
        return true
      }
      return false
    },
    [getCursorPosition]
  )

  // Helper: Handle backspace/delete in mention context
  const handleBackspaceDeleteInMention = useCallback(() => {
    setTimeout(() => {
      const position = getCursorPosition()
      if (position) {
        setMentionState((prev) => ({
          ...prev,
          position,
        }))
      }
    }, 0)
  }, [getCursorPosition])

  // Helper: Update mention search query
  const updateMentionSearchQuery = useCallback(
    (
      query: string,
      textarea: HTMLTextAreaElement,
      _start: number,
      _keyLength: number
    ) => {
      setTimeout(() => {
        const position = getCursorPosition()
        if (position) {
          // Get the current cursor position from textarea (after the character was inserted)
          const currentCursorPos = textarea.selectionStart
          setMentionState((prev) => ({
            ...prev,
            searchQuery: query,
            position,
          }))
          if (textarea) {
            textarea.focus()
            // Use the current cursor position, not the calculated one
            // The cursor should already be in the right place after the character was inserted
            textarea.setSelectionRange(currentCursorPos, currentCursorPos)
          }
        }
      }, 0)
    },
    [getCursorPosition]
  )

  // Helper: Process mention context in markdown mode
  const processMentionContextMarkdown = useCallback(
    (
      event: React.KeyboardEvent<HTMLTextAreaElement>,
      textarea: HTMLTextAreaElement,
      start: number,
      beforeText: string
    ): boolean => {
      const atIndex = beforeText.lastIndexOf('@')

      if (atIndex === -1) {
        closeMentionPopover()
        return false
      }

      // Get query from @ to current cursor position (before the key was pressed)
      let query = beforeText.substring(atIndex + 1, start)

      // Include current character if valid
      if (isValidSearchCharacter(event.key, event.ctrlKey, event.metaKey)) {
        query += event.key
      }

      const trimmedQuery = query.trim()

      // Close mention if space, newline, or special character
      if (shouldCloseMention(event.key)) {
        closeMentionPopover()
        return false
      }

      // Handle Backspace/Delete
      if (event.key === 'Backspace' || event.key === 'Delete') {
        handleBackspaceDeleteInMention()
        return true
      }

      // Update search query - use the cursor position after the character is inserted
      // We need to read it from the textarea after React updates, so use requestAnimationFrame
      requestAnimationFrame(() => {
        const currentCursorPos = textarea.selectionStart
        updateMentionSearchQuery(trimmedQuery, textarea, currentCursorPos, 0)
      })
      return true
    },
    [
      closeMentionPopover,
      handleBackspaceDeleteInMention,
      updateMentionSearchQuery,
    ]
  )

  const detectMentionMarkdown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const textarea = textareaRef.current
      if (!textarea) return false

      const start = textarea.selectionStart
      const beforeText = rawMarkdown.substring(0, start)

      // Check if @ was just typed
      if (event.key === '@') {
        return handleAtKeyInMarkdown(textarea, start)
      }

      // Check if we're in a mention context
      if (mentionState.isOpen || mentionTriggerRef.current !== null) {
        return processMentionContextMarkdown(event, textarea, start, beforeText)
      }

      return false
    },
    [
      rawMarkdown,
      mentionState.isOpen,
      handleAtKeyInMarkdown,
      processMentionContextMarkdown,
    ]
  )

  const renderElement = useCallback(
    (props: RenderElementProps) => <Element {...props} mode={mode} />,
    [mode]
  )

  const renderLeaf = useCallback(
    (props: RenderLeafProps) => <Leaf {...props} mode={mode} />,
    [mode]
  )

  // When focus lands and value is mention-only (PLAIN), normalize selection so cursor
  // moves from inside the mention to after it (enables typing)
  const handleFocus = useCallback(() => {
    setIsFocused(true)
    if (mode === EditorMode.PLAIN && isValueMentionOnly(value)) {
      requestAnimationFrame(() => {
        try {
          normalizeSelection()
        } catch {
          // ignore
        }
      })
    }
  }, [mode, value, normalizeSelection])

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      const clipboardEvent = event.nativeEvent
      const wasProcessed = processPaste(
        clipboardEvent,
        editor,
        maxCharacters,
        mode
      )

      if (wasProcessed) {
        event.preventDefault()
      }
    },
    [editor, maxCharacters, mode]
  )

  // Handle mention popover keyboard navigation
  const handleMentionPopoverKeys = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>) => {
      if (!mentionState.isOpen) {
        return false
      }

      if (
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Enter' ||
        event.key === 'Tab'
      ) {
        // Let MentionPopover handle these keys
        return true
      }

      if (event.key === 'Escape') {
        setMentionState({
          isOpen: false,
          searchQuery: '',
          position: null,
          triggerPosition: null,
        })
        mentionTriggerRef.current = null
        event.preventDefault()
        return true
      }

      return false
    },
    [mentionState.isOpen]
  )

  // Handle mention context detection when popover is open
  const handleMentionContextCheck = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>) => {
      if (!mentionState.isOpen) {
        return false
      }

      if (mode === EditorMode.MARKDOWN) {
        const isMentionContext = detectMentionMarkdown(
          event as React.KeyboardEvent<HTMLTextAreaElement>
        )
        if (isMentionContext) {
          return true
        }
      } else {
        const isMentionContext = detectMentionPlain(
          event as React.KeyboardEvent<HTMLDivElement>
        )
        if (isMentionContext) {
          return true
        }
        // If not in mention context but popover is open, close it
        // EXCEPT for Backspace/Delete - those are handled by detectMentionPlain and return true
        const isBackspaceOrDelete =
          event.key === 'Backspace' || event.key === 'Delete'
        if (!isBackspaceOrDelete) {
          setMentionState({
            isOpen: false,
            searchQuery: '',
            position: null,
            triggerPosition: null,
          })
          mentionTriggerRef.current = null
        }
      }

      return false
    },
    [mentionState.isOpen, mode, detectMentionMarkdown, detectMentionPlain]
  )

  // Handle @ key detection for markdown mode
  const handleAtKeyDetectionMarkdown = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const beforeText = rawMarkdown.substring(0, start)
    if (!beforeText.endsWith('@')) return

    const position = getCursorPosition()
    if (position) {
      setMentionState({
        isOpen: true,
        searchQuery: '',
        position,
        triggerPosition: start - 1,
      })
      mentionTriggerRef.current = start - 1
      textarea.focus()
      textarea.setSelectionRange(start, start)
    }
  }, [rawMarkdown, getCursorPosition])

  // Handle @ key detection for plain mode
  const handleAtKeyDetectionPlain = useCallback(() => {
    const { selection } = editor
    if (!selection) return

    const [start] = Range.edges(selection)
    const beforeText = Editor.string(editor, {
      anchor: Editor.start(editor, start.path),
      focus: start,
    })
    if (!beforeText.endsWith('@')) return

    const position = getCursorPosition()
    if (position) {
      setMentionState({
        isOpen: true,
        searchQuery: '',
        position,
        triggerPosition: start.offset,
      })
      mentionTriggerRef.current = start.offset
      requestAnimationFrame(() => {
        ReactEditor.focus(editor)
        try {
          Transforms.select(editor, selection)
        } catch {
          try {
            Transforms.select(editor, {
              anchor: start,
              focus: start,
            })
          } catch {
            ReactEditor.focus(editor)
          }
        }
      })
    }
  }, [editor, getCursorPosition])

  // Handle @ key detection for opening mention popover
  const handleAtKeyDetection = useCallback(() => {
    setTimeout(() => {
      if (mode === EditorMode.MARKDOWN) {
        handleAtKeyDetectionMarkdown()
      } else {
        handleAtKeyDetectionPlain()
      }
    }, 0)
  }, [mode, handleAtKeyDetectionMarkdown, handleAtKeyDetectionPlain])

  // Handle max character limit
  const handleMaxCharacterLimit = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>) => {
      if (!isMaximumCharacter) {
        return false
      }

      const allowedKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
        'Tab',
        ' ',
      ]

      const isHotkeyPressed = Object.keys(HOTKEYS).some((hotkey) =>
        isHotkey(hotkey, event.nativeEvent)
      )

      if (
        !allowedKeys.includes(event.key) &&
        !isHotkeyPressed &&
        !event.metaKey &&
        !event.ctrlKey
      ) {
        event.preventDefault()
        return true
      }

      return false
    },
    [isMaximumCharacter]
  )

  // Handle formatting hotkeys
  const handleFormattingHotkeys = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>) => {
      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, event.nativeEvent)) {
          event.preventDefault()
          const mark = HOTKEYS[hotkey]

          if (mode === EditorMode.MARKDOWN) {
            const syntaxMap: Record<string, string> = {
              bold: '**',
              italic: '*',
              code: '`',
            }
            const syntax = syntaxMap[mark]
            if (syntax) {
              handleInsertMarkdown(syntax, true)
            }
          } else {
            toggleMark(editor, mark)
          }
          return true
        }
      }
      return false
    },
    [mode, editor, handleInsertMarkdown]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>) => {
      // Handle mention popover keyboard navigation
      if (handleMentionPopoverKeys(event)) {
        return
      }

      // Detect mentions (after key is processed, not before)
      if (handleMentionContextCheck(event)) {
        return
      }

      // Handle @ key detection
      if (event.key === '@') {
        handleAtKeyDetection()
      }

      // Check max character limit
      if (handleMaxCharacterLimit(event)) {
        return
      }

      // Handle formatting hotkeys
      handleFormattingHotkeys(event)
    },
    [
      handleMentionPopoverKeys,
      handleMentionContextCheck,
      handleAtKeyDetection,
      handleMaxCharacterLimit,
      handleFormattingHotkeys,
    ]
  )

  // Render raw markdown textarea for markdown mode
  // Skip markdown mode for thread reply editor (only plain mode allowed)
  if (mode === EditorMode.MARKDOWN && id !== REPLY_EDITOR_ID) {
    return (
      <div className={cn('relative w-full h-full', className)}>
        <div className="flex flex-col h-full overflow-hidden border border-solid rounded-xl border-primary">
          <EditorToolbar
            disabled={isMaximumCharacter}
            mode={EditorMode.MARKDOWN}
            onMarkdownInsert={handleInsertMarkdown}
            onTemplateClick={() => setIsTemplateDialogOpen(true)}
          />
          <div className="relative flex-1">
            <textarea
              ref={textareaRef}
              id={id}
              value={rawMarkdown}
              onChange={handleRawMarkdownChange}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              autoFocus={autoFocus}
              className={cn(
                EDITOR_BASE_CLASSES,
                'pt-2 resize-none text-md h-full',
                showSendButton ? 'pb-4 pr-16' : ''
              )}
              spellCheck
              aria-label={t('tflViewer.addCommentDialog.editor.ariaLabel')}
              aria-describedby={ariaDescribedBy}
              aria-multiline="true"
            />
            {mentionState.isOpen && (
              <MentionPopover
                searchQuery={mentionState.searchQuery}
                position={mentionState.position}
                onSelect={handleMentionSelect}
                onClose={() => {
                  setMentionState({
                    isOpen: false,
                    searchQuery: '',
                    position: null,
                    triggerPosition: null,
                  })
                  mentionTriggerRef.current = null
                }}
              />
            )}
          </div>
        </div>
        <CommentTemplateDialog
          open={isTemplateDialogOpen}
          onOpenChange={setIsTemplateDialogOpen}
          onSelectTemplate={handleTemplateSelect}
        />
      </div>
    )
  }

  return (
    <Slate
      key={mode}
      editor={editor}
      initialValue={value}
      onValueChange={handleValueChange}
    >
      <div
        className={cn(
          'flex h-full flex-col rounded-xl border border-primary border-solid',
          className
        )}
      >
        <EditorToolbar
          disabled={isMaximumCharacter}
          toolbarIconSize={toolbarIconSize}
          onTemplateClick={() => setIsTemplateDialogOpen(true)}
        />
        <div className="relative h-[calc(100%-37px)]">
          <Editable
            id={id}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            onCompositionStart={onCompositionStart}
            onCompositionEnd={onCompositionEnd}
            onFocus={handleFocus}
            onBlur={() => setIsFocused(false)}
            disabled={disabled}
            autoFocus={autoFocus}
            className={cn(
              EDITOR_BASE_CLASSES,
              'h-full overflow-y-auto',
              showSendButton ? 'pb-4 pr-16 overflow-x-hidden' : '',
              showHorizontalScroll && !showSendButton ? 'overflow-x-hidden' : ''
            )}
            spellCheck
            aria-label={t('tflViewer.addCommentDialog.editor.ariaLabel')}
            aria-describedby={ariaDescribedBy}
            role="textbox"
            aria-multiline="true"
            placeholder={isFocused ? undefined : placeholder}
          />
          {mentionState.isOpen && (
            <MentionPopover
              searchQuery={mentionState.searchQuery}
              position={mentionState.position}
              onSelect={handleMentionSelect}
              onClose={() => {
                setMentionState({
                  isOpen: false,
                  searchQuery: '',
                  position: null,
                  triggerPosition: null,
                })
                mentionTriggerRef.current = null
              }}
            />
          )}
          {showSendButton && onSend && (
            <button
              type="button"
              onClick={onSend}
              disabled={disabled || isSending}
              className={cn(
                'absolute bottom-5 right-5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 bg-transparent border-none px-0 py-0 hover:font-bold',
                countCharacters(value) > 0
                  ? 'text-blue-600'
                  : 'text-quaternary-500'
              )}
            >
              {isSending ? 'Sending...' : 'Send'}
            </button>
          )}
        </div>
      </div>
      <CommentTemplateDialog
        open={isTemplateDialogOpen}
        onOpenChange={setIsTemplateDialogOpen}
        onSelectTemplate={handleTemplateSelect}
      />
    </Slate>
  )
}

const Element: React.FC<RenderElementProps & { mode: EditorModeType }> = ({
  attributes,
  children,
  element,
}) => {
  if (!SlateElement.isElement(element)) {
    return <p {...attributes}>{children}</p>
  }

  switch (element.type) {
    case ELEMENT_TYPE_BLOCK_QUOTE:
      return (
        <blockquote {...attributes} className={BLOCK_QUOTE_CLASSES}>
          {children}
        </blockquote>
      )
    case ELEMENT_TYPE_HEADING: {
      const level = element.level
      const headingClasses = [
        HEADING_BASE_CLASSES,
        HEADING_LEVEL_CLASSES[level as 1 | 2] || HEADING_LEVEL_CLASSES[2],
      ]
        .filter(Boolean)
        .join(' ')

      if (level === 1) {
        return (
          <h1 {...attributes} className={headingClasses}>
            {children}
          </h1>
        )
      }

      // Default to H2 for any other level
      return (
        <h2 {...attributes} className={headingClasses}>
          {children}
        </h2>
      )
    }
    case ELEMENT_TYPE_BULLETED_LIST:
      return (
        <ul {...attributes} className={`${LIST_BASE_CLASSES} list-disc`}>
          {children}
        </ul>
      )
    case ELEMENT_TYPE_NUMBERED_LIST:
      return (
        <ol {...attributes} className={`${LIST_BASE_CLASSES} list-decimal`}>
          {children}
        </ol>
      )
    case ELEMENT_TYPE_LIST_ITEM:
      return (
        <li {...attributes} className={LIST_ITEM_CLASSES}>
          {children}
        </li>
      )
    case ELEMENT_TYPE_MENTION:
      return (
        <MentionItem attributes={attributes} element={element}>
          {children}
        </MentionItem>
      )
    default:
      return (
        <p {...attributes} className={PARAGRAPH_CLASSES}>
          {children}
        </p>
      )
  }
}

const Leaf: React.FC<RenderLeafProps & { mode: EditorModeType }> = ({
  attributes,
  children,
  leaf,
  mode,
}) => {
  if (!Text.isText(leaf)) {
    return <span {...attributes}>{children}</span>
  }

  // Build class names and data attributes for markdown mode
  const isMarkdownMode = mode === EditorMode.MARKDOWN
  const classNames: string[] = []
  const dataAttributes: Record<string, string> = {}

  // In markdown mode, use data attributes and CSS pseudo-elements to show syntax
  if (isMarkdownMode) {
    if (leaf.bold) {
      classNames.push('markdown-bold')
      dataAttributes['data-md-bold'] = 'true'
    }
    if (leaf.italic) {
      classNames.push('markdown-italic')
      dataAttributes['data-md-italic'] = 'true'
    }
    if (leaf.code) {
      classNames.push('markdown-code')
      dataAttributes['data-md-code'] = 'true'
    }

    return (
      <span
        {...attributes}
        className={classNames.join(' ')}
        {...dataAttributes}
      >
        {children}
      </span>
    )
  }

  // Normal rendering mode with actual formatting
  let formattedChildren = children

  if (leaf.bold) {
    formattedChildren = (
      <strong className="font-bold">{formattedChildren}</strong>
    )
  }

  if (leaf.italic) {
    formattedChildren = <em className="italic">{formattedChildren}</em>
  }

  if (leaf.code) {
    formattedChildren = (
      <code className="px-1 py-1 font-mono text-sm text-gray-800 bg-gray-200 rounded">
        {formattedChildren}
      </code>
    )
  }

  return <span {...attributes}>{formattedChildren}</span>
}
